import { NewPageWrapper, RichTable, useTitle } from '../../common';
import { PermissionsComponent } from './Permissions.types';
import { TableData } from '@hiiretail/synergy-ui/dist/types/components/surfaces/Table/Table.types';
import { useEffect, useMemo } from 'react';
import { PermissionDto } from '../../api-client';
import { useGetPermissions } from './hooks/useGetPermissions';
import { FuseOptions } from 'fuse.js';
import { useSearch } from '../../common/hooks/useSearch/useSearch';
import { ErrorResponse } from '../../common/types/errorRespose';
import { useBreadcrumbsActions } from '../../common/components/Breadcrumbs/context/useBreadcrumbsActions';
import { Flexbox, Typography } from '@hiiretail/synergy-ui';
import { RefreshButton } from '../../common/components/Breadcrumbs/buttons/RefreshButton/RefreshButton';

export const Permissions: PermissionsComponent = () => {
  useTitle('Permissions');

  const { updateActions } = useBreadcrumbsActions();

  useEffect(() => {
    updateActions(
      <>
        <Flexbox item>
          <RefreshButton onClick={refetchPermissions} permission="iam.permission.list" />
        </Flexbox>
      </>,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: permissions,
    isFetching: isLoading,
    error: fetchError,
    refetch: refetchPermissions,
  } = useGetPermissions({
    retry: false,
  });

  const searchOptions: FuseOptions<PermissionDto> = useMemo(
    () => ({
      keys: ['id', 'description', 'alias'],
      caseSensitive: false,
      findAllMatches: true,
      threshold: 0.1,
    }),
    [],
  );

  const { filteredData: filteredPermissions, onSearchChange } = useSearch(
    permissions as PermissionDto[],
    searchOptions,
  );

  const tableColumns = useMemo(
    () => [
      { Header: 'ID', accessor: 'id' },
      {
        Header: 'Description',
        accessor: 'description',
        width: 200,
        Cell: ({
          row: { original },
        }: {
          row: {
            original: PermissionDto;
          };
        }) => <Typography>{original.description || 'Not set'}</Typography>,
      },
      {
        Header: 'Alias',
        accessor: 'alias',
        width: 200,
        Cell: ({
          row: { original },
        }: {
          row: {
            original: PermissionDto;
          };
        }) => <Typography>{original.alias || 'Not set'}</Typography>,
      },
    ],
    [],
  );

  return (
    <NewPageWrapper>
      <RichTable
        id="permissions-table"
        title="Browse permissions"
        columns={tableColumns}
        data={filteredPermissions as unknown as TableData[]}
        refetch={refetchPermissions}
        fetchError={fetchError as ErrorResponse | null}
        errorSubject="permissions"
        isLoading={isLoading}
        searchFieldLabel="Filter by id, description or alias"
        onSearchChange={onSearchChange}
        permissions={{
          create: '',
          list: 'iam.permission.list',
        }}
      />
    </NewPageWrapper>
  );
};
